import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, styled, Typography, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useRegisterPartnerMutation } from "Auth/Api/Mutations/useRegisterPartnerMutation";
import { BlFormCheckbox } from "Forms/Components/BlFormCheckbox";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlPhoneInput } from "Forms/Components/BlPhoneInput";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import { Spacing } from "Infrastructure/Themes/Themes";
import {
  getTranslation,
  Resources,
  useResource,
} from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { BlButton } from "Shared/Buttons/BlButton";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";
import { isPhoneValid } from "Shared/Utils/validations";

const RegisterPartnerSchema = z.object({
  firstName: z
    .string()
    .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
  lastName: z
    .string()
    .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
  company: z
    .string()
    .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
  login: z.email({
    error: () => getTranslation(Resources.Validation.InvalidEmail),
  }),
  phone: z
    .string()
    .min(1, { error: () => getTranslation(Resources.Validation.Required) })
    .refine(isPhoneValid, {
      error: () => getTranslation(Resources.Validation.InvalidPhone),
    }),
  termsAccepted: z.literal(true, {
    error: () => getTranslation(Resources.Validation.IsTrue),
  }),
});

type RegisterPartnerFormData = z.infer<typeof RegisterPartnerSchema>;

const Form = styled("form")`
  width: 100%;
  max-width: 400px;
`;

export const RegisterPartnerForm = (): React.ReactNode => {
  const theme = useTheme();
  const { t } = useResource();
  const { mutate, error, isSuccess } = useRegisterPartnerMutation();

  const form = useForm<RegisterPartnerFormData>({
    resolver: zodResolver(RegisterPartnerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      company: "",
      login: "",
      phone: "",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  useHandleServerError({
    form,
    error,
    formKey: "login",
    resource: Resources.SignUp.Error,
  });

  const submit = (data: RegisterPartnerFormData) => {
    mutate({
      firstName: data.firstName,
      lastName: data.lastName,
      company: data.company,
      login: data.login,
      phone: data.phone,
    });
  };

  if (isSuccess) {
    return (
      <Box sx={{ width: "100%", maxWidth: "400px" }}>
        <Typography variant="h1" mb={Spacing.M}>
          {t(Resources.SignUp.Success.Title)}
        </Typography>
        <Typography
          variant="body1"
          color={theme.palette.text.secondary}
          mb={Spacing.L}
        >
          {t(Resources.SignUp.Success.Description)}
        </Typography>
        <UnstyledLink to={AppRouting.SignIn.path()} isUnderlined>
          <Typography variant="body2">{t(Resources.SignIn.Button)}</Typography>
        </UnstyledLink>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "400px" }}>
      <Typography variant="h1" mb={Spacing.S}>
        {t(Resources.SignUp.Title)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={Spacing.L}>
        {t(Resources.SignUp.Subtitle)}
      </Typography>
      <Form onSubmit={handleSubmit(submit)}>
        <Stack spacing={Spacing.L}>
          <Stack spacing={Spacing.S}>
            <BlFormInput
              control={control}
              name="firstName"
              errors={errors}
              label={t(Resources.SignUp.FirstName.Label)}
              placeholder={t(Resources.SignUp.FirstName.Label)}
            />
            <BlFormInput
              control={control}
              name="lastName"
              errors={errors}
              label={t(Resources.SignUp.LastName.Label)}
              placeholder={t(Resources.SignUp.LastName.Label)}
            />
            <BlFormInput
              control={control}
              name="company"
              errors={errors}
              label={t(Resources.SignUp.Company.Label)}
              placeholder={t(Resources.SignUp.Company.Label)}
            />
            <Box sx={{ height: Spacing.XXXL }} />
            <BlFormInput
              control={control}
              name="login"
              errors={errors}
              type="email"
              autoComplete="email"
              label={t(Resources.SignUp.Email.Label)}
              placeholder={t(Resources.SignUp.Email.Label)}
            />
            <BlPhoneInput
              control={control}
              name="phone"
              errors={errors}
              label={t(Resources.SignUp.Phone.Label)}
              placeholder={t(Resources.SignUp.Phone.Label)}
            />
            <BlFormCheckbox
              control={control}
              name="termsAccepted"
              errors={errors}
              label={
                <Typography variant="body2" component="span">
                  {t(Resources.SignUp.TermsAndConditions)}{" "}
                  <UnstyledLink to="" isUnderlined>
                    {t(Resources.SignUp.TermsLink)}
                  </UnstyledLink>
                  .
                </Typography>
              }
            />
          </Stack>
          <Stack spacing={Spacing.XS}>
            <BlButton type="submit" fullWidth color="primary">
              {t(Resources.SignUp.Button)}
            </BlButton>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography display="inline-block" variant="body2">
                {t(Resources.SignUp.AlreadyHaveAccount)}
              </Typography>
              &nbsp;
              <UnstyledLink to={AppRouting.SignIn.path()} isUnderlined>
                <Typography variant="body2">
                  {t(Resources.SignUp.SignInNow)}
                </Typography>
              </UnstyledLink>
            </Box>
          </Stack>
        </Stack>
      </Form>
    </Box>
  );
};
